import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ShoppingBag, Sparkles, Loader2, Heart, Music, Square } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useProducts } from '../context/ProductContext';
import { useWishlist } from '../context/WishlistContext';
import { toast } from 'sonner';
import { useCart } from '../context/CartContext';
import ProductComments from '../components/ProductComments';
import { playHealingMusic, getProductToneLabel } from '../lib/healingChime';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProductDetail = () => {
  const { id } = useParams();
  const { getProductById, loading } = useProducts();
  const product = getProductById(id);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [manualImageSelect, setManualImageSelect] = useState(false);
  const [isPlayingMusic, setIsPlayingMusic] = useState(false);
  const musicHandleRef = useRef(null);
  const { addToCart } = useCart();
  const { isInWishlist, toggleWishlist } = useWishlist();
  const inWishlist = product ? isInWishlist(product.id) : false;

  // Set initial size when product loads
  useEffect(() => {
    if (product?.sizes?.[0] && !selectedSize) {
      setSelectedSize(product.sizes[0]);
    }
  }, [product, selectedSize]);

  // Track product view with product name
  useEffect(() => {
    if (product) {
      const trackProductView = async () => {
        try {
          await fetch(`${API_URL}/api/analytics/track`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              page: `/product/${product.id}`,
              product_id: product.id,
              product_name: product.name
            })
          });
        } catch (error) {
          // Analytics tracking is non-critical, log but don't interrupt user experience
          if (process.env.NODE_ENV === 'development') {
            console.warn('Analytics tracking failed:', error);
          }
        }
      };
      trackProductView();
    }
  }, [product]);

  // Stop music on unmount or when navigating away
  useEffect(() => {
    return () => {
      if (musicHandleRef.current) {
        musicHandleRef.current.stop();
        musicHandleRef.current = null;
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-950 to-purple-900 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-yellow-500 animate-spin" />
      </div>
    );
  }

  // Update main image when size changes
  const handleSizeSelect = (size) => {
    setSelectedSize(size);
    setManualImageSelect(false); // Reset manual selection when size changes
    // If size has a specific image, update the main display image
    if (size.image && product.images) {
      const imageIndex = product.images.indexOf(size.image);
      if (imageIndex !== -1) {
        setSelectedImage(imageIndex);
      }
    } else if (size.image) {
      // If size image exists but not in images array, set to 0 and use size image
      setSelectedImage(0);
    }
  };

  // Toggle healing music playback for this product
  const toggleHealingMusic = () => {
    if (isPlayingMusic) {
      musicHandleRef.current?.stop();
      musicHandleRef.current = null;
      setIsPlayingMusic(false);
      return;
    }
    const handle = playHealingMusic({
      productId: product.id,
      onEnd: () => {
        musicHandleRef.current = null;
        setIsPlayingMusic(false);
      },
    });
    musicHandleRef.current = handle;
    setIsPlayingMusic(true);
  };

  // Handle thumbnail click
  const handleThumbnailClick = (index) => {
    setSelectedImage(index);
    setManualImageSelect(true); // User manually selected an image
  };

  if (!product) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-950 to-purple-900 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Product Not Found</h2>
          <Link to="/products">
            <Button className="bg-yellow-500 hover:bg-yellow-600 text-purple-900">
              Back to Products
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const displayPrice = selectedSize ? selectedSize.price : product.basePrice;
  const productImages = product.images || [product.image];
  // Use gallery image if manually selected, otherwise use size-specific image
  const currentDisplayImage = manualImageSelect 
    ? productImages[selectedImage] 
    : ((selectedSize && selectedSize.image) || productImages[selectedImage]);

  const handleAddToCart = () => {
    if (!selectedSize) {
      toast.error('Please select a size');
      return;
    }
    addToCart(product, selectedSize);
    const sizeText = selectedSize ? ` (${selectedSize.size})` : '';
    toast.success(`${product.name}${sizeText} added to cart!`);
  };

  const handleWishlistToggle = async () => {
    const result = await toggleWishlist(product.id);
    if (result.success) {
      toast.success(result.message);
    } else {
      toast.error(result.message);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-950 via-purple-900 to-purple-950 py-20">
      <div className="container mx-auto px-4">
        {/* Back Button */}
        <Link to="/products" className="inline-flex items-center text-yellow-400 hover:text-yellow-300 mb-8">
          <ArrowLeft className="mr-2" />
          Back to Products
        </Link>

        {/* Product Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Images */}
          <div className="relative">
            <div className="sticky top-24">
              {/* Main Image */}
              <Card className="bg-purple-800/40 border-purple-600 overflow-hidden mb-4">
                <img 
                  src={currentDisplayImage} 
                  alt={product.name}
                  className="w-full h-[500px] object-cover"
                />
              </Card>
              
              {/* Image Thumbnails */}
              {productImages.length > 1 && (
                <div className="grid grid-cols-6 gap-2">
                  {productImages.map((img, index) => (
                    <button
                      key={`thumbnail-${index}-${img.slice(-10)}`}
                      onClick={() => handleThumbnailClick(index)}
                      className={`relative overflow-hidden rounded border-2 transition-all ${
                        selectedImage === index 
                          ? 'border-yellow-400' 
                          : 'border-purple-600 hover:border-purple-400'
                      }`}
                    >
                      <img 
                        src={img} 
                        alt={`${product.name} view ${index + 1}`}
                        className="w-full h-16 object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Product Info */}
          <div>
            <Badge className="bg-yellow-500 text-purple-900 mb-4">{product.category}</Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{product.name}</h1>
            <div className="flex items-center gap-4 mb-6">
              <span className="text-4xl font-bold text-white">${displayPrice.toFixed(2)}</span>
            </div>
            
            <p className="text-lg text-purple-200 mb-8 leading-relaxed">{product.description}</p>

            {/* Healing Music Player - plays this oil's solfeggio frequency */}
            {getProductToneLabel(product.id) && (
              <div className="mb-6">
                <Button
                  type="button"
                  onClick={toggleHealingMusic}
                  className={`w-full md:w-auto flex items-center gap-3 px-5 py-3 rounded-full border-2 transition-all ${
                    isPlayingMusic
                      ? 'bg-yellow-500/20 border-yellow-400 text-yellow-200 hover:bg-yellow-500/30'
                      : 'bg-purple-800/50 border-yellow-400/50 text-yellow-300 hover:bg-purple-700/60 hover:border-yellow-400'
                  }`}
                  data-testid="play-healing-music-btn"
                >
                  {isPlayingMusic ? (
                    <>
                      <Square className="w-4 h-4 fill-current" />
                      <span className="font-medium">
                        Playing {getProductToneLabel(product.id).hz} healing · tap to stop
                      </span>
                      <span className="flex gap-0.5 ml-1">
                        <span className="inline-block w-1 h-4 bg-yellow-300 animate-pulse rounded-sm" />
                        <span className="inline-block w-1 h-4 bg-yellow-300 animate-pulse rounded-sm" style={{ animationDelay: '0.15s' }} />
                        <span className="inline-block w-1 h-4 bg-yellow-300 animate-pulse rounded-sm" style={{ animationDelay: '0.3s' }} />
                      </span>
                    </>
                  ) : (
                    <>
                      <Music className="w-4 h-4" />
                      <span className="font-medium">
                        Tap to play 1-min {getProductToneLabel(product.id).hz} healing meditation
                      </span>
                      <Sparkles className="w-4 h-4" />
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Size Selection */}
            {product.sizes && product.sizes.length > 0 && (
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-white mb-4">Select Size</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {product.sizes.map((sizeOption) => (
                    <button
                      key={`size-${sizeOption.size}`}
                      onClick={() => handleSizeSelect(sizeOption)}
                      disabled={sizeOption.quantity === 0}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        selectedSize?.size === sizeOption.size
                          ? 'border-yellow-400 bg-yellow-500/10'
                          : sizeOption.quantity === 0
                          ? 'border-purple-800 bg-purple-900/50 opacity-50 cursor-not-allowed'
                          : 'border-purple-600 bg-purple-800/30 hover:border-purple-400'
                      }`}
                    >
                      <div className="text-white font-semibold">{sizeOption.size}</div>
                      <div className="text-purple-200 text-sm">${sizeOption.price.toFixed(2)}</div>
                      <div className={`text-xs mt-1 ${
                        sizeOption.quantity === 0 ? 'text-red-400' : 'text-green-400'
                      }`}>
                        {sizeOption.quantity === 0 ? 'Out of Stock' : 'Available'}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-4 mb-8">
              <Button 
                size="lg" 
                className="flex-1 md:flex-none bg-yellow-500 hover:bg-yellow-600 text-purple-900 font-semibold px-12 py-6 text-lg"
                onClick={handleAddToCart}
                disabled={product.sizes && product.sizes.length > 0 && (!selectedSize || selectedSize.quantity === 0)}
                data-testid="add-to-cart-btn"
              >
                <ShoppingBag className="mr-2" />
                {selectedSize && selectedSize.quantity === 0 ? 'Out of Stock' : 'Add to Cart'}
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={handleWishlistToggle}
                className={`px-6 py-6 border-2 ${
                  inWishlist 
                    ? 'border-red-400 bg-red-500/20 text-red-400' 
                    : 'border-purple-600 text-purple-200 hover:bg-purple-800'
                }`}
                data-testid="wishlist-btn"
              >
                <Heart className={`w-6 h-6 ${inWishlist ? 'fill-red-400' : ''}`} />
              </Button>
            </div>

            {/* Sacred Uses - Highlights the spiritual applications for this oil */}
            {product.uses && product.uses.length > 0 && (
              <Card className="bg-gradient-to-br from-yellow-500/15 to-purple-800/50 border-yellow-500/50 mb-6" data-testid="product-uses-card">
                <CardContent className="pt-6">
                  <h3 className="text-2xl font-semibold text-white mb-2 flex items-center">
                    <Sparkles className="mr-2 text-yellow-400" />
                    Sacred Uses
                  </h3>
                  <p className="text-purple-100 mb-4 text-sm">
                    Perfect for ritual oils, glamour magic, ceremonies, spiritual growth and healing.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {product.uses.map((use) => (
                      <span
                        key={`detail-use-${use}`}
                        className="px-3 py-1.5 rounded-full bg-yellow-500/20 text-yellow-200 border border-yellow-400/50 text-sm font-medium tracking-wide"
                        data-testid="product-use-pill"
                      >
                        ✦ {use}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Ingredients */}
            {product.ingredients && (
              <Card className="bg-purple-800/50 border-purple-600 mb-6">
                <CardContent className="pt-6">
                  <h3 className="text-2xl font-semibold text-white mb-4 flex items-center">
                    <Sparkles className="mr-2" />
                    Sacred Ingredients
                  </h3>
                  <ul className="space-y-2">
                    {product.ingredients.map((ingredient, index) => (
                      <li key={`ingredient-${ingredient.slice(0, 10)}-${index}`} className="text-purple-200 flex items-start">
                        <span className="text-yellow-400 mr-2">•</span>
                        {ingredient}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Benefits */}
            {product.benefits && (
              <Card className="bg-purple-800/50 border-purple-600 mb-6">
                <CardContent className="pt-6">
                  <h3 className="text-2xl font-semibold text-white mb-4">Spiritual Benefits</h3>
                  <ul className="space-y-2">
                    {product.benefits.map((benefit, index) => (
                      <li key={`benefit-${benefit.slice(0, 10)}-${index}`} className="text-purple-200 flex items-start">
                        <span className="text-yellow-400 mr-2">✦</span>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Usage */}
            {product.usage && (
              <Card className="bg-purple-800/50 border-purple-600">
                <CardContent className="pt-6">
                  <h3 className="text-2xl font-semibold text-white mb-4">How to Use</h3>
                  <p className="text-purple-200 leading-relaxed">{product.usage}</p>

                  {/* Spiritual-purpose disclaimer */}
                  <div
                    className="mt-6 pt-5 border-t border-purple-600/60"
                    data-testid="product-disclaimer"
                  >
                    <p className="text-purple-300/90 text-sm leading-relaxed italic">
                      <span className="text-yellow-300 font-semibold not-italic">
                        Disclaimer:
                      </span>{' '}
                      Infused by Daizy&apos;s products are not traditional perfume oils.
                      Each blend is spiritually charged with very specific intentions and is
                      not designed to serve as a long-lasting fragrance. These oils are meant
                      to assist you on your journey of healing and embracing your soul&apos;s beauty. ✦
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Customer Comments Section */}
        <div className="mt-12">
          <ProductComments productId={product.id} productName={product.name} />
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
